import React from 'react';
import styled from 'styled-components';
import { Check } from 'lucide-react';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Track = styled.div`
  position: relative;
  height: 6px;
  border-radius: ${(p) => p.theme.radiusPill};
  background: ${(p) => p.theme.border};
  overflow: hidden;
`;

const Fill = styled.div<{ $percent: number }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(p) => p.$percent}%;
  border-radius: ${(p) => p.theme.radiusPill};
  background: linear-gradient(90deg, ${(p) => p.theme.primary}, ${(p) => p.theme.petrol});
  transition: width 0.25s ease;
`;

const StepList = styled.ol`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-x: auto;
`;

const StepItem = styled.li<{ $state: 'done' | 'current' | 'todo'; $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex: 1 0 auto;
  min-width: 0;
  padding: 0.25rem 0;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  color: ${(p) =>
    p.$state === 'current' ? p.theme.primary : p.$state === 'done' ? p.theme.petrol : p.theme.textMuted};

  .bullet {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
    border-radius: 50%;
    font-size: 0.7rem;
    font-weight: 700;
    border: 1.5px solid currentColor;
    background: ${(p) => (p.$state === 'done' ? p.theme.petrol : 'transparent')};
    color: ${(p) => (p.$state === 'done' ? p.theme.textInverted : 'inherit')};
  }

  .label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  @media (max-width: 720px) {
    .label {
      display: ${(p) => (p.$state === 'current' ? 'inline' : 'none')};
    }
  }
`;

const Counter = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textMuted};
`;

export interface StepperStep {
  id: string;
  label: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Zero-based index of the step currently being shown. */
  currentIndex: number;
  /** When provided, already-completed steps become clickable shortcuts. */
  onStepClick?: (index: number) => void;
  /** Hides the "Passo X de Y" counter. */
  hideCounter?: boolean;
}

/** Horizontal progress indicator for multi-step flows (wizards). */
export const Stepper: React.FC<StepperProps> = ({ steps, currentIndex, onStepClick, hideCounter }) => {
  const total = Math.max(steps.length, 1);
  const percent = ((currentIndex + 1) / total) * 100;

  return (
    <Root>
      <StepList>
        {steps.map((step, index) => {
          const state = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo';
          const clickable = !!onStepClick && index < currentIndex;
          return (
            <StepItem
              key={step.id}
              $state={state}
              $clickable={clickable}
              aria-current={state === 'current' ? 'step' : undefined}
              onClick={clickable ? () => onStepClick?.(index) : undefined}
            >
              <span className="bullet">{state === 'done' ? <Check size={12} strokeWidth={3} /> : index + 1}</span>
              <span className="label">{step.label}</span>
            </StepItem>
          );
        })}
      </StepList>
      <Track>
        <Fill $percent={percent} />
      </Track>
      {!hideCounter && (
        <Counter>
          Passo {currentIndex + 1} de {total} - {steps[currentIndex]?.label}
        </Counter>
      )}
    </Root>
  );
};

export default Stepper;
